import React, { useMemo } from "react";
import {
  CalendarDays,
  Clock,
  User,
  Phone,
  CheckCircle2,
  AlertCircle,
  History,
  Save,
  Plus,
  Minus,
} from "lucide-react";

export default function AppointmentsTab({
  isAdmin,
  loading,
  appointments,
  showHistoryAppointments,
  onToggleHistory,
  processingAppointmentId,
  onCancelAppointment,
  getCustomerAvatarClassName,
  showManualAppointmentForm,
  onToggleManualAppointmentForm,
  manualAppointmentForm,
  onManualFormChange,
  staffList,
  availableManualServices,
  selectedManualStaff,
  selectedManualService,
  manualSlotsLoading,
  manualTimeSlots,
  onCreateManualAppointment,
  savingManualAppointment,
  minManualDate,
}) {
  const sortedAppointments = useMemo(() => {
    return [...appointments].sort((a, b) => {
      const dateComp = b.date.localeCompare(a.date);
      if (dateComp !== 0) return dateComp;
      return b.startTime.localeCompare(a.startTime);
    });
  }, [appointments]);

  return (
    <div>
      <div className="manual-appointment-card">
        <div className="manual-appointment-header">
          <h3>Manuel Randevu Ekle</h3>
          <button
            type="button"
            className="btn-add"
            onClick={onToggleManualAppointmentForm}
          >
            {showManualAppointmentForm ? <Minus /> : <Plus />}
            {showManualAppointmentForm ? "Formu Gizle" : "Randevu Ekle"}
          </button>
        </div>

        {showManualAppointmentForm && (
          <form className="manual-appointment-form" onSubmit={onCreateManualAppointment}>
            <div className="manual-appointment-grid">
              {isAdmin && (
                <div>
                  <label className="form-label">Çalışan</label>
                  <select
                    className="form-input"
                    value={manualAppointmentForm.staffId}
                    onChange={(e) => onManualFormChange("staffId", e.target.value)}
                    required
                  >
                    <option value="">Çalışan seçin</option>
                    {staffList.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.fullName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="form-label">Hizmet</label>
                <select
                  className={`form-input ${!selectedManualStaff ? "disabled" : ""}`}
                  value={manualAppointmentForm.serviceId}
                  onChange={(e) => onManualFormChange("serviceId", e.target.value)}
                  required
                  disabled={!selectedManualStaff}
                >
                  <option value="">Hizmet seçin</option>
                  {availableManualServices.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name} ({service.duration} dk)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Tarih</label>
                <input
                  type="date"
                  className="form-input"
                  value={manualAppointmentForm.date}
                  min={minManualDate}
                  onChange={(e) => onManualFormChange("date", e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="form-label">Saat</label>
                <select
                  className={`form-input ${!manualAppointmentForm.date || !selectedManualService || manualSlotsLoading ? "disabled" : ""}`}
                  value={manualAppointmentForm.startTime}
                  onChange={(e) => onManualFormChange("startTime", e.target.value)}
                  required
                  disabled={!manualAppointmentForm.date || !selectedManualService || manualSlotsLoading}
                >
                  <option value="">
                    {manualSlotsLoading ? "Uygun saatler yükleniyor..." : "Saat seçin"}
                  </option>
                  {manualTimeSlots
                    .filter((slot) => slot.available)
                    .map((slot) => (
                      <option key={slot.time} value={slot.time}>
                        {slot.time} - {slot.endTime}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="form-label">Müşteri Ad Soyad</label>
                <input
                  type="text"
                  className="form-input"
                  value={manualAppointmentForm.customerName}
                  onChange={(e) => onManualFormChange("customerName", e.target.value)}
                  placeholder="Örn: Ayşe Demir"
                  required
                />
              </div>

              <div>
                <label className="form-label">Müşteri Telefon</label>
                <input
                  type="tel"
                  className="form-input"
                  value={manualAppointmentForm.customerPhone}
                  onChange={(e) => onManualFormChange("customerPhone", e.target.value)}
                  placeholder="05xxxxxxxxx"
                  required
                />
              </div>
            </div>

            <div className="manual-appointment-actions">
              <button type="submit" className="btn-save" disabled={savingManualAppointment}>
                <Save />
                {savingManualAppointment ? "Kaydediliyor..." : "Randevuyu Oluştur"}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="admin-section-header">
        <h2 className="section-title" style={{ marginBottom: 0 }}>
          {isAdmin ? "Tüm Randevular" : "Randevularım"}
        </h2>
        <button type="button" className="btn-history-toggle" onClick={onToggleHistory}>
          <History />
          {showHistoryAppointments
            ? "Güncel Randevuları Görüntüle"
            : "Geçmiş Randevuları Görüntüle"}
        </button>
      </div>

      {loading ? (
        <div className="admin-empty">
          <CalendarDays />
          <p>Randevular yükleniyor...</p>
        </div>
      ) : sortedAppointments.length === 0 ? (
        <div className="admin-empty">
          <CalendarDays />
          <p>Henüz bir randevu bulunmamaktadır.</p>
        </div>
      ) : (
        <div className="admin-table-wrapper">
          <div className="admin-table-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Müşteri</th>
                  <th>Hizmet</th>
                  {isAdmin && <th>Çalışan</th>}
                  <th>Tarih</th>
                  <th>Saat</th>
                  <th>Durum</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {sortedAppointments.map((apt) => (
                  <tr key={apt.id}>
                    <td>
                      <div className="customer-cell">
                        <div className={getCustomerAvatarClassName(apt)}>
                          <User />
                        </div>
                        <div>
                          <div className="customer-name">{apt.customerName}</div>
                          <div className="customer-phone">
                            <Phone />
                            {apt.customerPhone}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: "0.875rem", width: "max-content" }}>{apt.serviceName || "-"}</td>
                    {isAdmin && (
                      <td>
                        <span className="staff-cell">{apt.staffName || "-"}</span>
                      </td>
                    )}
                    <td style={{ fontSize: "0.875rem", fontWeight: 500, width: "max-content" }}>{apt.date}</td>
                    <td>
                      <div className="time-cell">
                        <Clock />
                        {apt.startTime} - {apt.endTime}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${apt.status}`}>
                        {apt.status === "confirmed" ? <CheckCircle2 /> : <AlertCircle />}
                        {apt.status === "confirmed"
                          ? "Onaylı"
                          : apt.status === "pending"
                            ? "Beklemede"
                            : "İptal"}
                      </span>
                    </td>
                    <td>
                      {apt.status !== "cancelled" ? (
                        <button
                          onClick={() => onCancelAppointment(apt.id)}
                          className="btn-table-cancel"
                          disabled={processingAppointmentId === apt.id}
                        >
                          {processingAppointmentId === apt.id ? "İptal Ediliyor..." : "İptal Et"}
                        </button>
                      ) : (
                        <span className="table-muted">İptal edildi</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
